/**
 * The record feed's wire types and small helpers (spec 0014). One stream per record from
 * GET /api/activity/entity/:type/:id?include=notes,emails: events, comments and emails, each
 * item typed by `kind`, newest first.
 */

export interface FeedEvent {
  kind: 'event';
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  event_type?: string;
  field: string | null;
  message: string;
}

export interface FeedNote {
  kind: 'note';
  id: string;
  timestamp: string;
  author_user_id: string | null;
  author_name: string | null;
  content: string;
  parent_note_id: string | null;
  edited_at: string | null;
  pinned_at: string | null;
}

export interface FeedEmail {
  kind: 'email';
  id: string;
  timestamp: string;
  author_user_id: string | null;
  author_name: string | null;
  subject: string | null;
  to: string[];
  cc: string[];
  status: 'draft' | 'pushed' | 'failed' | 'pending' | 'sent' | 'skipped';
  channel: 'resend' | 'outlook_draft';
  draft_kind: string | null;
  provider_web_link: string | null;
  last_error: string | null;
  pinned_at: string | null;
}

export type FeedItem = FeedEvent | FeedNote | FeedEmail;

export interface RecordFeedResponse {
  events: unknown[];
  items: FeedItem[];
  limit: number;
  offset: number;
}

export const feedKey = (entityType: string, entityId: string) => ['record-feed', entityType, entityId] as const;

/** Plain text as the HTML body of an email: escaped, paragraphs on blank lines, <br> on newlines. */
export function textToHtml(text: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * The reverse, for resuming a saved draft in the plain-text composer. The result goes into a
 * textarea as text, never back into the page as HTML. Parsed with DOMParser (which runs no
 * scripts) rather than stripped with a tag regex.
 */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p').forEach((p) => p.append('\n\n'));
  return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * One email address, checked in a single character scan (the regex form backtracks polynomially
 * on crafted input). Mirrors src/utils/email-address.js: one '@', a non-empty local part, a
 * dotted domain, no whitespace or < > , ;, at most 254 characters.
 */
export function isEmailAddress(value: string): boolean {
  if (value.length === 0 || value.length > 254) return false;
  let at = -1;
  for (let i = 0; i < value.length; i++) {
    const ch = value[i];
    if (/\s/.test(ch) || ch === '<' || ch === '>' || ch === ',' || ch === ';') return false;
    if (ch === '@') {
      if (at !== -1) return false;
      at = i;
    }
  }
  if (at < 1) return false;
  const domain = value.slice(at + 1);
  return domain.length >= 3 && domain.slice(1, -1).includes('.');
}

/** The documents FK for a record type, when documents can hang off it. */
export const DOCUMENT_FK: Record<string, string | undefined> = {
  clients: 'client_id', properties: 'property_id', parcels: 'parcel_id', assessors: 'assessor_id', collectors: 'collector_id',
};

/** Record types a contact can be saved onto from the composer. */
export const CONTACT_HOSTS = new Set(['clients', 'assessors', 'collectors', 'jurisdictions', 'properties', 'parcels']);
